import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useQueries } from "@tanstack/react-query";
import { Chart } from "react-charts";
import { BadgeCheck, Boxes, CreditCard, FileText, Plus, ShieldAlert, TrendingUp, UserPlus, UsersRound, Wallet } from "lucide-react";

import { dashboard } from "src/api";
import { viewWalletBalance } from "src/api/wallet";
import { useAuth } from "src/providers/AuthProvider";
import useHasPermissions from "src/hooks/useHasPermissions";
import PermissionWrapper from "src/components/PermissionWrapper";
import CustomModal from "src/components/CustomModal";
import NewCustomerModal from "src/components/modals/NewCustomerModal";
import TopUpWallet from "src/components/modals/TopUpWallet";
import { Button } from "src/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/card";
import { DataTable, PageHead, StatCard, type Column } from "src/components/common";
import { cn, getCustomizationValue, renderCurrency, renderNumber, showInfoToast, toChartSeries } from "src/lib/utils";
import type { DashboardInvoicePreview, DashboardPaymentPreview, DashboardResponse, MonthlyOverview, WalletBalanceResponse } from "src/types/dashboard";

const formatDate = (value?: string) => {
	if (!value) return "—";
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? "—" : format(parsed, "MMM dd, yyyy");
};

const fullName = (row: { first_name?: string; last_name?: string }) =>
	[row.first_name, row.last_name].filter(Boolean).join(" ") || "—";

const Dashboard = () => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const canViewWalletBalance = useHasPermissions("view_wallet_balance");

	const [{ data }, { data: rawWalletBalance }] = useQueries({
		queries: [
			{ queryKey: ["dashboard"], queryFn: dashboard, suspense: true },
			{ queryKey: ["wallet-balance"], queryFn: viewWalletBalance, enabled: canViewWalletBalance, suspense: true },
		],
	}) as [{ data?: DashboardResponse }, { data?: WalletBalanceResponse }];

	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";
	const invoiceLabel = getCustomizationValue(user, "invoice_label") || "Invoices";
	const invoiceLabelSingular = getCustomizationValue(user, "invoice_label", true) || "Invoice";

	const dash = data?.data;
	const invoices = dash?.invoices ?? [];
	const payments = dash?.recent_payments ?? [];
	const walletBalance = rawWalletBalance?.data;

	const requireBvn = () => {
		if (!user?.bvn_verified) {
			showInfoToast("Please verify your BVN before proceeding");
			navigate("/account");
			return false;
		}
		return true;
	};

	const invoiceColumns: Column<DashboardInvoicePreview>[] = [
		{ key: "customer", header: `${customerLabel} Name`, cell: (row) => fullName(row), primary: true },
		{ key: "date", header: "Date Created", cell: (row) => formatDate(row.created_at) },
		{
			key: "amount",
			header: "Total Amount",
			className: "tw-text-right",
			cell: (row) => <span className="tw-font-semibold">{renderCurrency(row.total_amount)}</span>,
		},
	];

	const paymentColumns: Column<DashboardPaymentPreview>[] = [
		{ key: "customer", header: customerLabel, cell: (row) => fullName(row), primary: true },
		{
			key: "amount",
			header: "Amount",
			cell: (row) => <span className="tw-font-semibold">{renderCurrency(row.amount)}</span>,
		},
		{ key: "date", header: "Date Created", className: "tw-text-right", cell: (row) => formatDate(row.created_at) },
	];

	return (
		<div className="tw-space-y-5">
			<PageHead
				title={`Hello ${user?.first_name || "there"}`}
				subtitle="Here's how your business is doing today."
				actions={
					<Button
						className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark"
						size="sm"
						onClick={() => {
							if (requireBvn()) navigate("/invoices/add");
						}}
					>
						<Plus className="tw-h-4 tw-w-4" />
						New {invoiceLabelSingular}
					</Button>
				}
			/>

			{/*
				Prototype's insight banner. It nudged through Add Customer -> Add Product -> New Invoice,
				which needs customer/product counts the dashboard endpoint doesn't return. The BVN gate is
				a real blocking signal already enforced across this page, so the slot surfaces that instead.
			*/}
			{!user?.bvn_verified && (
				<div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-amber/30 tw-bg-amber-tint tw-p-4">
					<div className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-amber/15 tw-text-amber">
						<ShieldAlert className="tw-h-5 tw-w-5" />
					</div>
					<div className="tw-min-w-0 tw-flex-1 tw-text-[13.5px] tw-text-text">
						<span className="tw-mr-1.5 tw-rounded-full tw-bg-amber tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-bold tw-text-white">
							Action needed
						</span>
						Verify your BVN to start creating {invoiceLabel.toLowerCase()} and collecting payments.
					</div>
					<Button size="sm" className="tw-bg-navy tw-text-white hover:tw-bg-navy-deep" onClick={() => navigate("/account")}>
						Verify BVN
					</Button>
				</div>
			)}

			<PermissionWrapper permission="view_dashboard">
				<div className="tw-grid tw-gap-4 tw-grid-cols-1 sm:tw-grid-cols-2 xl:tw-grid-cols-4">
					<StatCard label="Wallet Balance" value={renderCurrency(walletBalance)} icon={Wallet} tone="teal" />
					<StatCard label="Total Unpaid Invoice" value={renderCurrency(dash?.account_receivable ?? 0)} icon={FileText} tone="blue" />
					<StatCard label="Total Payments Received" value={renderCurrency(dash?.payment_received ?? 0)} icon={CreditCard} tone="navy" />
					<StatCard label="Cumulative Revenue" value={renderCurrency(dash?.cumulative_revenue ?? 0)} icon={TrendingUp} tone="amber" />
				</div>

				<div className="tw-mt-4 tw-grid tw-gap-4 tw-grid-cols-1 sm:tw-grid-cols-3">
					<StatCard label="Sales" value={renderNumber(dash?.total_sales_count ?? 0)} icon={BadgeCheck} tone="teal" />
					<StatCard label="Products Sold" value={renderNumber(dash?.total_products_sold ?? 0)} icon={Boxes} tone="blue" />
					<StatCard label="New Customers" value={renderNumber(dash?.new_customers_count ?? 0)} icon={UsersRound} tone="amber" />
				</div>
			</PermissionWrapper>

			<div className="tw-grid tw-gap-4 tw-grid-cols-1 xl:tw-grid-cols-[1fr,320px]">
				<OverviewChart monthlyData={dash?.monthly_overview ?? []} />
				<QuickActions onRequireBvn={requireBvn} />
			</div>

			<div className="tw-grid tw-gap-4 tw-grid-cols-1 xl:tw-grid-cols-2">
				<PermissionWrapper permission="view_invoices">
					<section>
						<div className="tw-mb-2.5 tw-flex tw-items-center tw-justify-between">
							<h3 className="tw-text-[15px] tw-font-bold tw-text-text">{invoiceLabel}</h3>
							<Link
								to="/invoices"
								className="tw-rounded-lg tw-border tw-border-border tw-bg-white tw-px-3 tw-py-1.5 tw-text-[12.5px] tw-font-semibold tw-text-text tw-transition-colors hover:tw-bg-bg"
							>
								View all
							</Link>
						</div>
						<DataTable
							data={invoices}
							columns={invoiceColumns}
							rowKey={(row, index) => row.uuid ?? index}
							rowsPerPageOptions={[5, 10, "All"]}
							defaultRowsPerPage={5}
							emptyTitle={`No ${invoiceLabel.toLowerCase()} yet`}
							emptyDescription={`${invoiceLabel} you raise will appear here.`}
						/>
					</section>
				</PermissionWrapper>

				<PermissionWrapper permission="view_payment">
					<section>
						<div className="tw-mb-2.5 tw-flex tw-items-center tw-justify-between">
							<h3 className="tw-text-[15px] tw-font-bold tw-text-text">Payments</h3>
							<Link
								to="/payments"
								className="tw-rounded-lg tw-border tw-border-border tw-bg-white tw-px-3 tw-py-1.5 tw-text-[12.5px] tw-font-semibold tw-text-text tw-transition-colors hover:tw-bg-bg"
							>
								View all
							</Link>
						</div>
						<DataTable
							data={payments}
							columns={paymentColumns}
							rowKey={(row, index) => row.uuid ?? index}
							rowsPerPageOptions={[5, 10, "All"]}
							defaultRowsPerPage={5}
							emptyTitle="No payments yet"
							emptyDescription="Confirmed payments will show up here."
						/>
					</section>
				</PermissionWrapper>
			</div>
		</div>
	);
};

interface ChartDatum {
	primary: string;
	secondary: number;
}

const OverviewChart = ({ monthlyData }: { monthlyData: MonthlyOverview[] }) => {
	const primaryAxis = useMemo(() => ({ getValue: (datum: ChartDatum) => datum.primary }), []);
	const secondaryAxes = useMemo(() => [{ getValue: (datum: ChartDatum) => datum.secondary }], []);
	const series = toChartSeries(monthlyData);

	return (
		<Card>
			<CardHeader>
				<CardTitle>An Overview of Invoices and Payments</CardTitle>
				<div className="tw-flex tw-items-center tw-gap-3 tw-text-[12px] tw-text-text-soft">
					<span className="tw-flex tw-items-center tw-gap-1.5">
						<i className="tw-h-2.5 tw-w-2.5 tw-rounded-sm tw-bg-teal" />
						Invoices
					</span>
					<span className="tw-flex tw-items-center tw-gap-1.5">
						<i className="tw-h-2.5 tw-w-2.5 tw-rounded-sm tw-bg-navy" />
						Payments
					</span>
				</div>
			</CardHeader>
			<CardContent>
				{monthlyData.length === 0 ? (
					<div className="tw-flex tw-h-[300px] tw-items-center tw-justify-center tw-text-[13px] tw-text-text-soft">
						No activity to chart yet.
					</div>
				) : (
					<div className="tw-h-[300px] tw-w-full">
						<Chart
							options={{
								data: series,
								primaryAxis,
								secondaryAxes,
								defaultColors: ["#149C86", "#0B1E3F"],
							}}
						/>
					</div>
				)}
			</CardContent>
		</Card>
	);
};

const quickActionClass =
	"tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-[10px] tw-border tw-border-border tw-bg-white tw-p-2.5 tw-text-[13.5px] tw-font-medium tw-text-text tw-transition-colors hover:tw-border-teal/40 hover:tw-bg-teal-tint";

const quickActionIconClass = "tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-teal-tint tw-text-teal";

/**
 * Occupies the prototype's "Recent Activity" slot. A real activity feed needs an
 * audit-log endpoint, which doesn't exist (spec §2.17 is prototype-only, in-memory),
 * so the existing Quick Actions panel is kept and restyled rather than faked.
 */
const QuickActions = ({ onRequireBvn }: { onRequireBvn: () => boolean }) => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";
	const invoiceLabelSingular = getCustomizationValue(user, "invoice_label", true) || "Invoice";

	return (
		<Card className="tw-h-fit">
			<CardHeader>
				<CardTitle>Quick Actions</CardTitle>
			</CardHeader>
			<CardContent className="tw-grid tw-gap-2.5">
				<CustomModal title={`Add Single ${customerLabel}`} trigger={
					<button type="button" className={quickActionClass}>
						<span className={quickActionIconClass}>
							<UserPlus className="tw-h-4 tw-w-4" />
						</span>
						Add new {customerLabel.toLowerCase()}
					</button>
				}>
					<NewCustomerModal />
				</CustomModal>

				<button
					type="button"
					className={quickActionClass}
					onClick={() => {
						if (onRequireBvn()) navigate("/invoices/add");
					}}
				>
					<span className={quickActionIconClass}>
						<FileText className="tw-h-4 tw-w-4" />
					</span>
					Create {invoiceLabelSingular.toLowerCase()}
				</button>

				<button type="button" className={quickActionClass} onClick={() => navigate("/customers")}>
					<span className={quickActionIconClass}>
						<UsersRound className="tw-h-4 tw-w-4" />
					</span>
					View all {customerLabel.toLowerCase()}s
				</button>

				<CustomModal title="Top Up Wallet" trigger={
					<button type="button" className={cn(quickActionClass)} onClick={() => onRequireBvn()}>
						<span className={quickActionIconClass}>
							<Wallet className="tw-h-4 tw-w-4" />
						</span>
						Top-up wallet
					</button>
				}>
					<TopUpWallet />
				</CustomModal>
			</CardContent>
		</Card>
	);
};

export default Dashboard;
